import { ArrowLeft, ArrowUpRight, BarChart3, Bell, Bold, BookmarkPlus, CheckCircle, ChevronLeft, ChevronRight, Circle as CircleIcon, Clock3, Crop, Download, Euro, Eye, EyeOff, FileText, Folder, GitBranch, GitMerge, History, ImagePlus, Italic, MessageSquare, Pencil, Plus, RefreshCw, RotateCcw, RotateCw, Search, Sparkles, Square, Trash2, Type, Undo2, Upload, Users, Wand2, X, Zap } from "lucide-react";
import katex from "katex";
import "katex/dist/katex.min.css";
import { Link, useParams } from "react-router-dom";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api, streamGuttenbergEvents } from "./api";
import { useAuth } from "../Auth";
import "./Guttenberg.css";
import { ImageDiffColumn, MermaidPreview, chartPreviewBlockFromMarkdown, chartSpecDiffItems, commentsForBlock, diffSummary, isObsoleteSuggestionForSection, lineSimilarity, markdownImageRefs, renderCommentedText, renderInlineMarkdown, reportImageSrc, smartDiffRows, tablePreviewBlocksFromMarkdown } from "./GuttenbergImageAndDiff";
import { ChartPreview, blockDeleteLabel, blockToMarkdown, blockVersionHistoryFor, capitalizeCaption, chartBlockToMarkdown, chartTitle, cleanEditableText, formatDateTime, imageBlockToMarkdown, inlineMarkdownFromEditable, inputQueueCommentLabel, orderedListBlockToMarkdown, parseMarkdownBlocks, sourceLabel, tableBlockToMarkdown, tableStructureToMarkdown, versionActionLabel, versionBlockLabel, versionTooltip } from "./GuttenbergChartMarkdown";
import { imageProcessingKey, sectionHeadings, slugify } from "./GuttenbergPageUtils";

function ReportOutlinePanel({ report, sections }) {
    const items = report?.manifest.sections ?? [];
    return (<section className="panel report-outline-panel">
      <h2>Plan du rapport</h2>
      {items.length ? (<nav className="report-outline-nav" aria-label="Plan du rapport">
          {items.map((section, index) => {
                const headings = sectionHeadings(sections[section.id] ?? "", section.subsections ?? []);
                return (<div className="outline-nav-section" key={section.id}>
                <a href={`#section-${section.id}`}>
                  <span>{index + 1}</span>
                  {section.title}
                </a>
                {!!headings.length && (<div className="outline-nav-subsections">
                    {headings.map((heading) => (<a href={`#${section.id}-${slugify(heading)}`} key={heading}>
                        {heading}
                      </a>))}
                  </div>)}
              </div>);
            })}
        </nav>) : (<div className="muted">Le plan apparaitra ici après génération.</div>)}
    </section>);
}

function VersionPanel({ versions, milestones, milestoneLabel, setMilestoneLabel, busy, onCreateMilestone, onRestoreMilestone, onRestoreBlock, onRestoreSection, projectId, embedded = false }) {
    const [selectedVersionId, setSelectedVersionId] = useState(versions[0]?.id ?? null);
    useEffect(() => {
        if (!versions.length) {
            setSelectedVersionId(null);
            return;
        }
        if (!selectedVersionId || !versions.some((version) => version.id === selectedVersionId)) {
            setSelectedVersionId(versions[0].id);
        }
    }, [selectedVersionId, versions]);
    const timeline = versions.slice(0, 50);
    const selectedVersion = versions.find((version) => version.id === selectedVersionId) ?? versions[0] ?? null;
    const selectedIndex = selectedVersion ? versions.findIndex((version) => version.id === selectedVersion.id) : -1;
    const newerVersion = selectedIndex > 0 ? versions[selectedIndex - 1] : null;
    const olderVersion = selectedIndex >= 0 && selectedIndex < versions.length - 1 ? versions[selectedIndex + 1] : null;
    const content = (<>
      {!embedded && (<div className="section-heading">
          <h2>Versions</h2>
          <span className="muted">{versions.length} changement(s)</span>
        </div>)}
      <div className="milestone-form">
        <input value={milestoneLabel} onChange={(event) => setMilestoneLabel(event.target.value)} placeholder="Nom du jalon"/>
        <button className="button secondary compact-button" disabled={busy} onClick={onCreateMilestone} type="button">
          <BookmarkPlus size={15}/> Jalon
        </button>
      </div>
      {!!milestones.length && (<div className="version-group">
          <strong>Jalons</strong>
          <ul className="version-list">
            {milestones.slice(0, 8).map((milestone) => (<li key={milestone.id}>
                <div>
                  <span>{milestone.label}</span>
                  <small>{milestone.source === "assistant" ? "Assistant - " : ""}{formatDateTime(milestone.created_at)} - {milestone.section_count} section(s)</small>
                  {(milestone.prompt || milestone.note) && <small title={milestone.note || milestone.prompt}>{milestone.prompt || milestone.note}</small>}
                </div>
                <button className="button secondary icon-button" disabled={busy} onClick={() => onRestoreMilestone(milestone.id)} title="Restaurer ce jalon" type="button">
                  <RotateCcw size={15}/>
                </button>
              </li>))}
          </ul>
        </div>)}
      <div className="version-group">
        <strong>Modifications du contenu</strong>
        {timeline.length ? (<ul className="version-list">
            {timeline.map((version) => (<li className={selectedVersion?.id === version.id ? "active" : ""} key={version.id}>
                <button className="version-select-button" onClick={() => setSelectedVersionId(version.id)} type="button">
                  <span>{version.section_title || version.section_id}</span>
                  <small>{sourceLabel(version.source)} - {formatDateTime(version.created_at)}</small>
                  {version.origin && <p>{version.origin}</p>}
                </button>
                <button className="button secondary icon-button" disabled={busy} onClick={() => onRestoreSection(version)} title="Restaurer toute la section avant cette modification" type="button">
                  <History size={15}/>
                </button>
              </li>))}
          </ul>) : (<div className="empty-comments muted">Les modifications du contenu redige apparaitront ici.</div>)}
      </div>
      {selectedVersion && (<div className="version-detail">
          <div className="version-popover-head">
            <span>{sourceLabel(selectedVersion.source)} - {formatDateTime(selectedVersion.created_at)}</span>
            <strong>{selectedVersion.section_title || selectedVersion.section_id}</strong>
            <small>{versionActionLabel(selectedVersion.action)}</small>
          </div>
          <div className="version-nav">
            <button className="button secondary compact-button" disabled={!newerVersion} onClick={() => newerVersion && setSelectedVersionId(newerVersion.id)} type="button">
              Plus recente
            </button>
            <button className="button secondary compact-button" disabled={!olderVersion} onClick={() => olderVersion && setSelectedVersionId(olderVersion.id)} type="button">
              Plus ancienne
            </button>
          </div>
          {selectedVersion.origin && <p className="version-origin">{selectedVersion.origin}</p>}
          <div className="version-block-list">
            {selectedVersion.changed_blocks.length ? (selectedVersion.changed_blocks.slice(0, 4).map((block) => (<VersionBlockPreview block={block} busy={busy} key={block.id} projectId={projectId} version={selectedVersion} onRestoreBlock={onRestoreBlock}/>))) : (<div className="empty-comments muted">Aucun bloc detaille pour cette version.</div>)}
            {selectedVersion.changed_blocks.length > 4 && (<small className="muted">{selectedVersion.changed_blocks.length - 4} autre(s) bloc(s) modifies.</small>)}
          </div>
          <div className="version-actions">
            <button className="button secondary compact-button" disabled={busy} type="button" onClick={() => onRestoreSection(selectedVersion)}>
              <RotateCcw size={15}/> Restaurer toute la section
            </button>
          </div>
        </div>)}
    </>);
    if (embedded)
        return <div className="version-panel">{content}</div>;
    return <section className="panel version-panel">{content}</section>;
}

function VersionBlockPreview({ version, block, busy, projectId, onRestoreBlock }) {
    const beforeText = block.previous_text || block.previous_markdown;
    const afterText = block.current_text || block.current_markdown;
    const beforeDiff = block.previous_markdown || block.previous_text;
    const afterDiff = block.current_markdown || block.current_text;
    return (<div className="version-block-preview">
      <div className="version-block-head">
        <strong>{versionBlockLabel(block)}</strong>
        <button className="button compact-button" disabled={busy} type="button" onClick={() => onRestoreBlock(version, block)}>
          <RotateCcw size={15}/> Restaurer ce bloc
        </button>
      </div>
      <VersionTextSnapshots before={beforeText} after={afterText}/>
      <SuggestionDiff before={beforeDiff} after={afterDiff} projectId={projectId}/>
    </div>);
}

function VersionTextSnapshots({ before, after }) {
    return (<div className="version-snapshots">
      <div className="version-snapshot">
        <span>Avant</span>
        <pre>{before.trim() || "Bloc absent"}</pre>
      </div>
      <div className="version-snapshot">
        <span>Apres</span>
        <pre>{after.trim() || "Bloc absent"}</pre>
      </div>
    </div>);
}

function reportPreviewFontFaceCss() {
    return [
        ["PlusJakartaSans-Regular.ttf", 400, "normal"],
        ["PlusJakartaSans-Medium.ttf", 500, "normal"],
        ["PlusJakartaSans-SemiBold.ttf", 600, "normal"],
        ["PlusJakartaSans-Bold.ttf", 700, "normal"],
        ["PlusJakartaSans-Italic.ttf", 400, "italic"],
        ["PlusJakartaSans-BoldItalic.ttf", 700, "italic"]
    ].map(([filename, weight, style]) => `@font-face {
        font-family: 'Plus Jakarta Sans';
        src: url('${api.innowideFontUrl(filename)}') format('truetype');
        font-weight: ${weight};
        font-style: ${style};
        font-display: swap;
    }`).join("\n");
}

function reportPaginationRevision(report, sections) {
    const manifest = report?.manifest ?? {};
    const payload = JSON.stringify({
        title: manifest.title,
        reportType: manifest.report_type,
        client: manifest.client,
        author: manifest.author,
        date: manifest.date,
        clientLogo: manifest.client_logo_path,
        sections: (manifest.sections ?? []).map((section) => [section.id, section.title, sections[section.id] ?? ""])
    });
    let hash = 2166136261;
    for (let index = 0; index < payload.length; index += 1) {
        hash ^= payload.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
    }
    return `${payload.length}-${hash >>> 0}`;
}

function paginateBlockHeights(items, capacity) {
    if (!items.length)
        return [[]];
    const pageCapacity = Math.max(1, Number(capacity) || 1);
    const pages = [];
    let page = [];
    let used = 0;
    const flush = () => {
        if (page.length)
            pages.push(page);
        page = [];
        used = 0;
    };
    items.forEach((item, position) => {
        const height = Math.max(0, Number(item.height) || 0);
        const isHeading = item.type === "h1" || item.type === "h2";
        const nextHeight = isHeading && position + 1 < items.length
            ? Math.max(0, Number(items[position + 1].height) || 0)
            : 0;
        if (page.length && used + height + nextHeight > pageCapacity + 1)
            flush();
        if (page.length && used + height > pageCapacity + 1)
            flush();
        page.push(item.index);
        used += height;
    });
    flush();
    return pages.length ? pages : [[]];
}

function reportTocEntries(sectionModels) {
    return sectionModels.flatMap((sectionModel) => {
        const headings = sectionModel.blocks.flatMap((block, blockIndex) => {
            if ((block.type !== "h1" && block.type !== "h2") || !block.text?.trim())
                return [];
            return [{
                key: `${sectionModel.section.id}:${blockIndex}`,
                sectionId: sectionModel.section.id,
                blockIndex,
                level: block.type === "h1" ? 0 : 1,
                title: block.text.trim()
            }];
        });
        if (headings.length)
            return headings;
        return [{
            key: `${sectionModel.section.id}:section`,
            sectionId: sectionModel.section.id,
            blockIndex: null,
            level: 0,
            title: sectionModel.section.title
        }];
    });
}

function fallbackReportPagination(sectionModels, tocEntries, sourceRevision) {
    let pageNumber = 3;
    return {
        sourceRevision,
        tocPages: [tocEntries.map((entry) => ({ ...entry, pageNumber: pageNumber + sectionModels.findIndex((model) => model.section.id === entry.sectionId) }))],
        sectionPages: sectionModels.map((sectionModel) => ({
            sectionId: sectionModel.section.id,
            blockIndexes: sectionModel.blocks.map((_, index) => index),
            pageNumber: pageNumber++,
            partIndex: 0
        }))
    };
}

function ReportAttributionToggle({ visible, saving = false, onToggle }) {
    return (<button
      className={`button secondary ${visible ? "active" : ""}`}
      type="button"
      aria-pressed={visible}
      disabled={saving}
      onClick={onToggle}
      title={visible ? "Masquer la mention Gutenberg de la couverture" : "Afficher la mention Gutenberg sur la couverture"}
    >
      {visible ? <Eye size={17}/> : <EyeOff size={17}/>}
      {saving ? "Enregistrement..." : visible ? "Mention IA affichée" : "Mention IA masquée"}
    </button>);
}

function FullReportPreview({ report, sections, projectId, commentMode, editMode, comments, sectionVersions, activeVersion, assistantVersionIds = [], processingCommentIds, processingImageKeys, processingSectionIds, savingSectionIds, selectedBankImage, collaborationLocks, currentSessionId, onTextSelection, onImageSelection, onImageEditSelection, onChartComment, onImageAutoRetouch, onInsertionPoint, onImageReplaceUpload, onImageReplaceWithBank, onImageCrop, onAcceptSuggestion, onRejectSuggestion, onActiveVersionChange, onRestoreBlock, onRestoreSection, onBlockEdit, onInsertBlock, onDeleteBlock, onCreateSection, onDeleteSection, onEditFocus, onEditBlur }) {
    const manifest = report.manifest;
    const clientLogo = manifest.client_logo_path;
    const sectionModels = useMemo(() => manifest.sections.map((section) => {
        const markdown = sections[section.id] ?? `# ${section.title}\n\n*En attente...*`;
        return { section, markdown, blocks: parseMarkdownBlocks(markdown) };
    }), [manifest.sections, sections]);
    const tocEntries = useMemo(() => reportTocEntries(sectionModels), [sectionModels]);
    const sourceRevision = useMemo(() => reportPaginationRevision(report, sections), [report, sections]);
    const measurementRef = useRef(null);
    const [pagination, setPagination] = useState(null);
    const fallbackPagination = fallbackReportPagination(sectionModels, tocEntries, sourceRevision);
    const layout = pagination?.sourceRevision === sourceRevision ? pagination : fallbackPagination;
    const reportAiLocked = collaborationLocks.some((lock) => lock.kind === "ai" && lock.scope === "report");

    useLayoutEffect(() => {
        const root = measurementRef.current;
        if (!root)
            return undefined;
        let animationFrame = 0;
        let disposed = false;
        const measure = () => {
            if (disposed)
                return;
            const pageBodyMeasure = root.querySelector("[data-page-body-measure]");
            const measuredPageCapacity = pageBodyMeasure?.getBoundingClientRect().height ?? 0;
            if (measuredPageCapacity < 1)
                return;
            // Keep one rendered line as a layout tolerance. Browser font
            // metrics and editable wrappers can otherwise place the final line
            // against the running footer even when the raw sums are equal.
            const measuredLineHeight = Number.parseFloat(window.getComputedStyle(root).lineHeight) || 16;
            const pageCapacity = Math.max(1, measuredPageCapacity - measuredLineHeight);
            const tocTitleHeight = root.querySelector("[data-toc-title-measure]")?.getBoundingClientRect().height ?? 0;
            const tocItems = Array.from(root.querySelectorAll("[data-toc-entry-measure]")).map((element, index) => ({
                index,
                type: "toc",
                height: element.getBoundingClientRect().height
            }));
            const tocGroups = paginateBlockHeights(tocItems, Math.max(1, pageCapacity - tocTitleHeight));
            let pageNumber = 2 + tocGroups.length;
            const sectionPages = [];
            const blockPageNumbers = new Map();
            const measureSections = Array.from(root.querySelectorAll("[data-measure-section-id]"));
            sectionModels.forEach((sectionModel) => {
                const sectionNode = measureSections.find((node) => node.dataset.measureSectionId === sectionModel.section.id);
                const blockElements = sectionNode
                    ? Array.from(sectionNode.children).filter((element) => element.classList.contains("markdown-block"))
                    : [];
                const sectionRect = sectionNode?.getBoundingClientRect();
                const blockRects = blockElements.map((element) => element.getBoundingClientRect());
                const measuredBlocks = blockElements.length
                    ? blockElements.map((element, index) => ({
                        index: Number(element.dataset.markdownBlockIndex),
                        type: element.dataset.paginationBlockType,
                        height: Math.max(0,
                            blockRects[index].height
                            + blockRects[index].top
                            - (index === 0 ? sectionRect.top : blockRects[index - 1].bottom)
                            + (index + 1 === blockRects.length ? sectionRect.bottom - blockRects[index].bottom : 0))
                    }))
                    : sectionModel.blocks.map((block, index) => ({ index, type: block.type, height: 0 }));
                paginateBlockHeights(measuredBlocks, pageCapacity).forEach((blockIndexes, partIndex) => {
                    sectionPages.push({ sectionId: sectionModel.section.id, blockIndexes, pageNumber, partIndex });
                    blockIndexes.forEach((blockIndex) => blockPageNumbers.set(`${sectionModel.section.id}:${blockIndex}`, pageNumber));
                    if (partIndex === 0)
                        blockPageNumbers.set(`${sectionModel.section.id}:section`, pageNumber);
                    pageNumber += 1;
                });
            });
            const tocPages = tocGroups.map((entryIndexes) => entryIndexes.map((entryIndex) => ({
                ...tocEntries[entryIndex],
                pageNumber: blockPageNumbers.get(tocEntries[entryIndex].key)
                    ?? blockPageNumbers.get(`${tocEntries[entryIndex].sectionId}:section`)
                    ?? 0
            })));
            const layoutKey = JSON.stringify({
                toc: tocPages.map((page) => page.map((entry) => [entry.key, entry.pageNumber])),
                sections: sectionPages.map((page) => [page.sectionId, page.blockIndexes])
            });
            setPagination((current) => current?.sourceRevision === sourceRevision && current.layoutKey === layoutKey
                ? current
                : { sourceRevision, tocPages, sectionPages, layoutKey });
        };
        const scheduleMeasure = () => {
            cancelAnimationFrame(animationFrame);
            animationFrame = requestAnimationFrame(measure);
        };
        scheduleMeasure();
        const observer = typeof ResizeObserver === "function" ? new ResizeObserver(scheduleMeasure) : null;
        observer?.observe(root);
        const images = Array.from(root.querySelectorAll("img"));
        images.forEach((image) => {
            image.addEventListener("load", scheduleMeasure);
            image.addEventListener("error", scheduleMeasure);
        });
        document.fonts?.ready?.then(scheduleMeasure).catch(() => {});
        return () => {
            disposed = true;
            cancelAnimationFrame(animationFrame);
            observer?.disconnect();
            images.forEach((image) => {
                image.removeEventListener("load", scheduleMeasure);
                image.removeEventListener("error", scheduleMeasure);
            });
        };
    }, [sectionModels, sourceRevision, tocEntries]);
    return (<article className={`paper-preview paginated-report ${commentMode ? "comment-mode" : ""} ${editMode ? "edit-mode" : ""} ${assistantVersionIds.length ? "assistant-review-mode" : ""}`} onMouseUp={onTextSelection} onKeyUp={onTextSelection}>
      <style>{reportPreviewFontFaceCss()}</style>
      <div className="report-pagination-measure" ref={measurementRef} aria-hidden="true">
        <div className="report-page-body-measure" data-page-body-measure/>
        <div className="report-toc-measure">
          <h1 data-toc-title-measure>Sommaire</h1>
          {tocEntries.map((entry) => (<div className={`paper-toc-entry level-${entry.level}`} data-toc-entry-measure key={`measure-${entry.key}`}>
              <span>{entry.title}</span><span className="paper-toc-dots"/><span>1</span>
            </div>))}
        </div>
        {sectionModels.map((sectionModel) => (<section className="report-measure-section" data-measure-section-id={sectionModel.section.id} key={`measure-${sectionModel.section.id}`}>
            <MarkdownBlock markdown={sectionModel.markdown} measurementMode projectId={projectId} sectionId={sectionModel.section.id} commentMode={false} editMode={editMode} comments={[]} versions={[]} activeVersion={null} processingCommentIds={{}} processingImageKeys={{}} selectedBankImage={null}/>
          </section>))}
      </div>
      <div className="paper-page paper-cover-page">
        <div className="paper-cover-document">
          <div className="paper-cover-brand-row">
            <img className="paper-cover-logo" src={api.innowideCoverLogoUrl()} alt="Innowide"/>
            {clientLogo && <img className="paper-client-logo" src={reportImageSrc(projectId, clientLogo)} alt={`Logo ${manifest.client || "client"}`}/>}
          </div>
          <div className="paper-cover-main">
            <div className="paper-cover-accent"/>
            <p className="paper-cover-type">{manifest.report_type}</p>
            <h1 className="paper-cover-title">{manifest.title}</h1>
          </div>
          <div className="paper-cover-info">
            <div><span>Client</span><strong>{manifest.client}</strong></div>
            <div><span>Auteur</span><strong>{manifest.author}</strong></div>
            <div><span>Date</span><strong>{manifest.date}</strong></div>
          </div>
          {manifest.show_guttenberg_attribution !== false && (<p className="paper-cover-note">Rapport préparé par les équipes Innowide avec l’appui de Gutenberg, outil d’assistance IA développé en interne par Innowide.</p>)}
        </div>
      </div>
      {layout.tocPages.map((entries, tocIndex) => (<div className="paper-page paper-toc-page" key={`toc-page-${tocIndex}`}>
          <div className="paper-page-header"><img className="paper-page-header-logo" src={api.innowideHeaderLogoUrl()} alt="Innowide"/></div>
          <div className="paper-page-content">
            <h1>Sommaire</h1>
            <nav className="paper-toc-list">
              {entries.map((entry) => (<a className={`paper-toc-entry level-${entry.level}`} href={entry.level === 1 ? `#${entry.sectionId}-${slugify(entry.title)}` : `#section-${entry.sectionId}`} key={entry.key}>
                  <span>{entry.title}</span><span className="paper-toc-dots"/><span>{entry.pageNumber}</span>
                </a>))}
            </nav>
          </div>
          <footer className="paper-page-footer"><span>Innowide - {manifest.title}</span><span>Page {tocIndex + 2}</span></footer>
        </div>))}
      {layout.sectionPages.map((page) => {
            const section = manifest.sections.find((item) => item.id === page.sectionId);
            if (!section)
                return null;
            const sectionModel = sectionModels.find((item) => item.section.id === section.id);
            const sectionComments = comments.filter((comment) => comment.section_id === section.id);
            const sectionHistory = sectionVersions.filter((version) => version.section_id === section.id);
            const activeSectionComments = sectionComments.filter((comment) => comment.status !== "applied" && comment.status !== "dismissed");
            const sectionProcessing = Boolean(processingSectionIds[section.id]);
            const sectionSaving = Boolean(savingSectionIds[section.id]);
            const sectionLocks = collaborationLocks.filter((lock) => lock.section_id === section.id);
            const sectionAiLocks = sectionLocks.filter((lock) => lock.kind === "ai" && lock.scope === "section");
            const remoteEditors = sectionLocks.filter((lock) => lock.kind === "user-edit" && lock.session_id !== currentSessionId);
            const sectionLocked = reportAiLocked || sectionProcessing || sectionAiLocks.length > 0 || remoteEditors.length > 0;
            const lockLabel = reportAiLocked
                ? "Rapport verrouille pendant une operation IA"
                : sectionAiLocks.length
                    ? sectionAiLocks[0].label || "IA en train de modifier cette section"
                    : remoteEditors.length
                    ? `${remoteEditors.map((lock) => lock.display_name).join(", ")} edite cette section`
                    : "";
            const firstSectionPage = page.partIndex === 0;
            return (<div className="paper-page" key={`${section.id}-${page.partIndex}`}>
            <div className="paper-page-header">
              <img className="paper-page-header-logo" src={api.innowideHeaderLogoUrl()} alt="Innowide"/>
            </div>
            <div className="paper-page-content">
            <section className={`paper-section ${sectionProcessing || sectionSaving ? "section-processing" : ""}`} data-section-id={section.id} id={firstSectionPage ? `section-${section.id}` : undefined}>
              {firstSectionPage && editMode && !sectionLocked && (<SectionStructureToolbar section={section} canDelete={manifest.sections.length > 1} onCreateSection={onCreateSection} onDeleteSection={onDeleteSection}/>)}
              {firstSectionPage && sectionLocked && lockLabel && (<div className="section-lock-banner">
                  <Users size={14}/> {lockLabel}
                </div>)}
              {firstSectionPage && sectionProcessing && (<div className="section-processing-banner">
                  <span /> IA en train de modifier cette section
                </div>)}
              {firstSectionPage && sectionSaving && (<div className="section-processing-banner">
                  <span /> Enregistrement de l'edition
                </div>)}
              {firstSectionPage && !!activeSectionComments.length && (<div className="inline-comments">
                  {activeSectionComments.map((comment) => (<span key={comment.id} className={comment.status} title={comment.comment}>
                      {comment.author === "review_agent" ? "Relecture" : "Commentaire"} · {comment.status}
                    </span>))}
                </div>)}
              <MarkdownBlock markdown={sectionModel?.markdown ?? ""} blockIndexes={page.blockIndexes} projectId={projectId} sectionId={section.id} commentMode={commentMode} editMode={editMode && !sectionLocked} comments={sectionComments} versions={sectionHistory} activeVersion={activeVersion} assistantVersionIds={assistantVersionIds} processingCommentIds={processingCommentIds} processingImageKeys={processingImageKeys} selectedBankImage={selectedBankImage} onImageSelection={onImageSelection} onImageEditSelection={onImageEditSelection} onChartComment={onChartComment} onImageAutoRetouch={onImageAutoRetouch} onInsertionPoint={onInsertionPoint} onImageReplaceUpload={onImageReplaceUpload} onImageReplaceWithBank={onImageReplaceWithBank} onImageCrop={onImageCrop} onAcceptSuggestion={onAcceptSuggestion} onRejectSuggestion={onRejectSuggestion} onActiveVersionChange={onActiveVersionChange} onRestoreBlock={onRestoreBlock} onRestoreSection={onRestoreSection} onBlockEdit={onBlockEdit} onInsertBlock={onInsertBlock} onDeleteBlock={onDeleteBlock} onEditFocus={onEditFocus} onEditBlur={onEditBlur}/>
            </section>
            </div>
            <footer className="paper-page-footer">
              <span>Innowide - {manifest.title}</span>
              <span>Page {page.pageNumber}</span>
            </footer>
          </div>);
        })}
    </article>);
}

function SectionStructureToolbar({ section, canDelete, onCreateSection, onDeleteSection }) {
    const [adding, setAdding] = useState(false);
    const [title, setTitle] = useState("");
    function submit(event) {
        event.preventDefault();
        const clean = title.trim();
        if (!clean)
            return;
        onCreateSection?.(section.id, clean);
        setTitle("");
        setAdding(false);
    }
    return (<div className="section-structure-toolbar">
      {adding ? (<form onSubmit={submit}>
          <input autoFocus value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Titre de la nouvelle partie" aria-label="Titre de la nouvelle partie"/>
          <button type="submit">Ajouter</button>
          <button type="button" onClick={() => setAdding(false)} aria-label="Annuler"><X size={14}/></button>
        </form>) : (<button type="button" onClick={() => setAdding(true)}><Plus size={14}/> Ajouter une partie après</button>)}
      <button className="danger" type="button" disabled={!canDelete} onClick={() => onDeleteSection?.(section)} title={canDelete ? "Supprimer toute cette partie" : "Le rapport doit conserver au moins une partie"}><Trash2 size={14}/> Supprimer la partie</button>
    </div>);
}

function OutlineLivePreview({ title, sections, running, raw }) {
    const totalPages = sections.reduce((sum, section) => sum + (section.estimated_pages ?? 0), 0);
    return (<article className="paper-preview outline-preview">
      <div className="paper-cover">
        <div className="brand">Innowide</div>
        <h1>{title}</h1>
        <div className="muted">{running ? "Planification en cours..." : "Plan propose"}</div>
      </div>
      <h1>Plan du rapport</h1>
      {!!sections.length && (<div className="outline-summary">
          {sections.length} section(s)
          {totalPages > 0 ? ` - ${totalPages.toFixed(1)} page(s) estimee(s)` : ""}
        </div>)}
      {sections.length ? (sections.map((section, index) => (<section className="outline-section" key={`${section.id}-${index}`}>
            <div className="outline-section-head">
              <h2>{section.title || `Section ${index + 1}`}</h2>
              {section.estimated_pages ? <span>{section.estimated_pages} p.</span> : null}
            </div>
            {section.brief && <p>{section.brief}</p>}
            {!!section.subsections.length && (<ul>
                {section.subsections.map((item, itemIndex) => (<li key={`${section.id}-live-subsection-${itemIndex}`}>{item}</li>))}
              </ul>)}
          </section>))) : (<div className="outline-placeholder">
          {raw ? "Lecture du plan en cours..." : "L'agent chef analyse les sources et prepare le plan."}
        </div>)}
    </article>);
}

function MarkdownBlock({ markdown, blockIndexes, measurementMode = false, projectId, sectionId, commentMode, editMode, comments, versions, activeVersion, assistantVersionIds = [], processingCommentIds, processingImageKeys, selectedBankImage, onImageSelection, onImageEditSelection, onChartComment, onImageAutoRetouch, onInsertionPoint, onImageReplaceUpload, onImageReplaceWithBank, onImageCrop, onAcceptSuggestion, onRejectSuggestion, onActiveVersionChange, onRestoreBlock, onRestoreSection, onBlockEdit, onInsertBlock, onDeleteBlock, onEditFocus, onEditBlur }) {
    const blocks = parseMarkdownBlocks(markdown);
    const visibleBlocks = Array.isArray(blockIndexes)
        ? blockIndexes.map((index) => ({ block: blocks[index], index })).filter((item) => item.block)
        : blocks.map((block, index) => ({ block, index }));
    const [tableCellToFocus, setTableCellToFocus] = useState(null);
    const assistantVersionIdSet = new Set(assistantVersionIds);
    const removedAssistantMatchesByIndex = new Map();
    versions.forEach((version) => {
        if (!assistantVersionIdSet.has(version.id) || !blocks.length)
            return;
        (version.changed_blocks ?? []).filter((item) => item.change_type === "removed").forEach((removedBlock) => {
            const anchorIndex = Math.max(0, Math.min(removedBlock.block_index, blocks.length - 1));
            const matches = removedAssistantMatchesByIndex.get(anchorIndex) ?? [];
            matches.push({ version, block: removedBlock });
            removedAssistantMatchesByIndex.set(anchorIndex, matches);
        });
    });
    function applyTableStructure(block, blockIndex, operation, origin, focusColumn = null) {
        if (operation.type === "delete_row" && !window.confirm("Supprimer cette ligne du tableau ?"))
            return;
        if (operation.type === "delete_column") {
            if (block.headers.length <= 1)
                return;
            if (!window.confirm("Supprimer cette colonne du tableau ?"))
                return;
        }
        if (focusColumn !== null)
            setTableCellToFocus(`${blockIndex}:${focusColumn}`);
        onBlockEdit(sectionId, blockIndex, tableStructureToMarkdown(block, operation), origin);
    }
    return (<>
      {visibleBlocks.map(({ block, index }) => {
            const blockComments = commentsForBlock(block, comments, index);
            const blockHistory = blockVersionHistoryFor(versions, index, block);
            const versionMatch = blockHistory[0] ?? null;
            const removedAssistantMatches = removedAssistantMatchesByIndex.get(index) ?? [];
            const activeRemovedMatch = removedAssistantMatches.find((item) => activeVersion?.versionId === item.version.id && activeVersion.blockId === item.block.id) ?? null;
            const assistantChangeMatch = activeRemovedMatch
                ?? blockHistory.find((item) => assistantVersionIdSet.has(item.version.id))
                ?? removedAssistantMatches[0]
                ?? null;
            const markerMatch = versionMatch ?? assistantChangeMatch;
            const activeHistoryIndex = blockHistory.findIndex((item) => activeVersion?.versionId === item.version.id &&
                activeVersion.blockId === item.block.id);
            const activeHistoryItem = activeRemovedMatch ?? (activeHistoryIndex >= 0 ? blockHistory[activeHistoryIndex] : versionMatch);
            const isVersionOpen = Boolean(activeHistoryItem &&
                activeVersion?.versionId === activeHistoryItem.version.id &&
                activeVersion.blockId === activeHistoryItem.block.id);
            const activeDisplayHistory = activeRemovedMatch ? [activeRemovedMatch] : blockHistory;
            const activeDisplayIndex = activeRemovedMatch ? 0 : activeHistoryIndex;
            let content;
            if (block.type === "h1") {
                content = (<EditableText as="h1" text={block.text} editable={editMode} onEditFocus={() => onEditFocus?.(sectionId, index)} onEditBlur={() => onEditBlur?.(sectionId, index)} onSave={(value) => onBlockEdit(sectionId, index, blockToMarkdown(block, value), `Edition du titre "${block.text.slice(0, 80)}"`)}>
              {renderCommentedText(block.text, blockComments)}
            </EditableText>);
            }
            else if (block.type === "h2") {
                content = (<EditableText as="h2" id={measurementMode ? undefined : `${sectionId}-${slugify(block.text)}`} text={block.text} editable={editMode} onEditFocus={() => onEditFocus?.(sectionId, index)} onEditBlur={() => onEditBlur?.(sectionId, index)} onSave={(value) => onBlockEdit(sectionId, index, blockToMarkdown(block, value), `Edition du sous-titre "${block.text.slice(0, 80)}"`)}>
              {renderCommentedText(block.text, blockComments)}
            </EditableText>);
            }
            else if (block.type === "image") {
                const displayCaption = capitalizeCaption(block.caption);
                const localImageProcessing = Boolean(processingImageKeys?.[imageProcessingKey(sectionId, block.path)]);
                const imageProcessing = localImageProcessing || blockComments.some((comment) => comment.target_type === "image_edit" &&
                    (processingCommentIds[comment.id] || comment.edit_suggestion?.status === "pending"));
                content = (<figure className={`preview-figure ${imageProcessing ? "image-processing" : ""}`}>
              <img className={commentMode ? "commentable-image" : ""} src={reportImageSrc(projectId, block.path)} alt={block.alt} onMouseUp={(event) => {
                        if (commentMode)
                            event.stopPropagation();
                    }} onClick={(event) => {
                        if (!commentMode)
                            return;
                        event.stopPropagation();
                        onImageSelection(sectionId, block.path, event.currentTarget.getBoundingClientRect(), index);
                    }}/>
              {imageProcessing && (<div className="image-processing-overlay">
                  <span /> Retouche image en cours
                </div>)}
              {(commentMode || editMode) && (<div className="image-action-palette">
                  {commentMode && (<>
                      <button className="button secondary compact-button" onClick={(event) => {
                                event.stopPropagation();
                                onImageSelection(sectionId, block.path, event.currentTarget.closest("figure")?.getBoundingClientRect() ?? event.currentTarget.getBoundingClientRect(), index);
                            }} type="button">
                        <MessageSquare size={14}/> Commenter
                      </button>
                      <button className="button compact-button" onClick={(event) => {
                                event.stopPropagation();
                                onImageEditSelection(sectionId, block.path, event.currentTarget.closest("figure")?.getBoundingClientRect() ?? event.currentTarget.getBoundingClientRect(), index);
                            }} type="button">
                        <Wand2 size={14}/> Retoucher
                      </button>
                      <button className="button compact-button" onClick={(event) => {
                                event.stopPropagation();
                                onImageAutoRetouch?.(sectionId, block.path, index);
                            }} type="button" title="Rend automatiquement le fond de l'image propre et professionnel">
                        <Sparkles size={14}/> Fond pro
                      </button>
                    </>)}
                  {editMode && (<>
                      <button className="button secondary compact-button" onClick={(event) => {
                                event.stopPropagation();
                                onImageCrop(sectionId, block.path, displayCaption || capitalizeCaption(block.alt));
                            }} type="button">
                        <Pencil size={14}/> Editer
                      </button>
                      <label className="button secondary compact-button image-replace-upload">
                        <ImagePlus size={14}/> Remplacer
                        <input hidden type="file" accept="image/*" onChange={(event) => {
                                event.stopPropagation();
                                const file = event.target.files?.[0];
                                if (file)
                                    onImageReplaceUpload(sectionId, block.path, file);
                                event.target.value = "";
                            }}/>
                      </label>
                      <button className="button compact-button" onClick={(event) => {
                                event.stopPropagation();
                                onImageAutoRetouch?.(sectionId, block.path, index);
                            }} type="button" title="Rend automatiquement le fond de l'image propre et professionnel">
                        <Sparkles size={14}/> Fond pro
                      </button>
                      {selectedBankImage && (<button className="button compact-button" onClick={(event) => {
                                    event.stopPropagation();
                                    onImageReplaceWithBank(sectionId, block.path);
                                }} type="button">
                          Remplacer selection
                        </button>)}
                    </>)}
                </div>)}
              {block.caption && (<EditableText as="figcaption" text={displayCaption} editable={editMode} onEditFocus={() => onEditFocus?.(sectionId, index)} onEditBlur={() => onEditBlur?.(sectionId, index)} onSave={(value) => onBlockEdit(sectionId, index, imageBlockToMarkdown(block, value), `Edition de la legende ${block.path}`)}>
                  {renderCommentedText(displayCaption, blockComments)}
                </EditableText>)}
            </figure>);
            }
            else if (block.type === "bullet") {
                content = (<EditableText as="p" className="bullet" text={block.text} editable={editMode} onEditFocus={() => onEditFocus?.(sectionId, index)} onEditBlur={() => onEditBlur?.(sectionId, index)} onSave={(value) => onBlockEdit(sectionId, index, blockToMarkdown(block, value), `Edition d'une puce`)}>
              {renderCommentedText(block.text, blockComments)}
            </EditableText>);
            }
            else if (block.type === "ordered_list") {
                content = (<ol className="markdown-ordered-list">
              {block.items.map((item, itemIndex) => (<li key={itemIndex}>
                  <EditableText as="p" text={`${item.title}${item.title && item.body ? " " : ""}${item.body}`} editable={editMode} onEditFocus={() => onEditFocus?.(sectionId, index)} onEditBlur={() => onEditBlur?.(sectionId, index)} onSave={(value) => onBlockEdit(sectionId, index, orderedListBlockToMarkdown(block, itemIndex, value), `Edition d'un item de liste`)}>
                    {item.title && <strong>{item.title}</strong>}
                    {item.title && item.body ? " " : ""}
                    {item.body ? renderCommentedText(item.body, blockComments) : null}
                  </EditableText>
                </li>))}
            </ol>);
            }
            else if (block.type === "visual_request") {
                content = (<div className="visual-request-placeholder">
              <strong>{block.title}</strong>
              {block.description && <p>{block.description}</p>}
            </div>);
            }
            else if (block.type === "visual_asset") {
                content = (<div className="visual-request-placeholder visual-asset-placeholder">
              <strong>{block.caption || "Visuel selectionne"}</strong>
              {block.path && <p>{block.path}</p>}
            </div>);
            }
            else if (block.type === "table") {
                content = (<div className="markdown-table-wrap">
              {editMode && (<div className="table-structure-toolbar">
                  <button className="button secondary compact-button" type="button" onClick={() => applyTableStructure(block, index, { type: "add_row", rowIndex: block.rows.length - 1, position: "after" }, "Ajout d'une ligne au tableau") }>
                    <Plus size={13}/> Ligne
                  </button>
                  <button className="button secondary compact-button" type="button" onClick={() => applyTableStructure(block, index, { type: "add_column", columnIndex: block.headers.length - 1, position: "right" }, "Ajout d'une colonne au tableau", block.headers.length)}>
                    <Plus size={13}/> Colonne
                  </button>
                </div>)}
              <table className="markdown-table">
                <thead>
                  {editMode && (<tr className="table-column-controls">
                      {block.headers.map((_, cellIndex) => (<th key={`controls-${cellIndex}`}>
                          <button type="button" title="Ajouter une colonne a gauche" onClick={() => applyTableStructure(block, index, { type: "add_column", columnIndex: cellIndex, position: "left" }, "Ajout d'une colonne a gauche", cellIndex)}>+ G</button>
                          <button type="button" title="Supprimer cette colonne" disabled={block.headers.length <= 1} onClick={() => applyTableStructure(block, index, { type: "delete_column", columnIndex: cellIndex }, "Suppression d'une colonne du tableau")}><Trash2 size={12}/></button>
                          <button type="button" title="Ajouter une colonne a droite" onClick={() => applyTableStructure(block, index, { type: "add_column", columnIndex: cellIndex, position: "right" }, "Ajout d'une colonne a droite", cellIndex + 1)}>D +</button>
                        </th>))}
                      <th className="table-row-action-spacer"/>
                    </tr>)}
                  <tr>
                    {block.headers.map((cell, cellIndex) => (<EditableTableCell as="th" key={cellIndex} text={cell} editable={editMode} alignment={block.alignments[cellIndex]} autoFocus={tableCellToFocus === `${index}:${cellIndex}`} onAutoFocusComplete={() => setTableCellToFocus(null)} onEditFocus={() => onEditFocus?.(sectionId, index)} onEditBlur={() => onEditBlur?.(sectionId, index)} onSave={(value) => onBlockEdit(sectionId, index, tableBlockToMarkdown(block, -1, cellIndex, value), `Edition d'un en-tete de tableau`)}/>))}
                    {editMode && <th className="table-row-action-spacer"/>}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, rowIndex) => (<tr key={rowIndex}>
                      {row.map((cell, cellIndex) => (<EditableTableCell as="td" key={cellIndex} text={cell} editable={editMode} alignment={block.alignments[cellIndex]} onEditFocus={() => onEditFocus?.(sectionId, index)} onEditBlur={() => onEditBlur?.(sectionId, index)} onSave={(value) => onBlockEdit(sectionId, index, tableBlockToMarkdown(block, rowIndex, cellIndex, value), `Edition d'une cellule de tableau`)}/>))}
                      {editMode && (<td className="table-row-controls">
                          <button type="button" title="Ajouter une ligne avant" onClick={() => applyTableStructure(block, index, { type: "add_row", rowIndex, position: "before" }, "Ajout d'une ligne avant")}>+ H</button>
                          <button type="button" title="Supprimer cette ligne" onClick={() => applyTableStructure(block, index, { type: "delete_row", rowIndex }, "Suppression d'une ligne du tableau")}><Trash2 size={12}/></button>
                          <button type="button" title="Ajouter une ligne apres" onClick={() => applyTableStructure(block, index, { type: "add_row", rowIndex, position: "after" }, "Ajout d'une ligne apres")}>B +</button>
                        </td>)}
                    </tr>))}
                </tbody>
              </table>
            </div>);
            }
            else if (block.type === "math_block") {
                let html;
                try {
                    html = katex.renderToString(block.latex, { displayMode: true, throwOnError: false, strict: false });
                }
                catch {
                    html = `<code>${block.latex}</code>`;
                }
                content = <div className="math-display" dangerouslySetInnerHTML={{ __html: html }}/>;
            }
            else if (block.type === "mermaid") {
                content = <MermaidPreview code={block.code}/>;
            }
            else if (block.type === "chart") {
                content = (<div className="chart-comment-shell">
              <ChartPreview block={block} projectId={projectId} sectionId={sectionId} editable={editMode} measureOnly={measurementMode} onEditFocus={() => onEditFocus?.(sectionId, index)} onEditBlur={() => onEditBlur?.(sectionId, index)} onSave={(spec) => onBlockEdit(sectionId, index, chartBlockToMarkdown(spec), `Edition du graphe "${chartTitle(spec).slice(0, 80)}"`)}/>
              {(commentMode || editMode) && (<div className="image-action-palette chart-action-palette">
                  <button className="button secondary compact-button" onClick={(event) => {
                            event.stopPropagation();
                            onChartComment?.(sectionId, block, event.currentTarget.closest(".chart-comment-shell")?.getBoundingClientRect() ?? event.currentTarget.getBoundingClientRect(), index);
                        }} type="button" title="Demander une retouche IA ciblee sur ce graphe">
                    <MessageSquare size={14}/> Retoucher
                  </button>
                </div>)}
            </div>);
            }
            else if (block.type === "blockquote") {
                content = (<blockquote>
              {block.lines.map((quoteLine, lineIdx) => (<p key={lineIdx}>{renderInlineMarkdown(quoteLine)}</p>))}
            </blockquote>);
            }
            else {
                content = (<EditableText as="p" text={block.text} editable={editMode} onEditFocus={() => onEditFocus?.(sectionId, index)} onEditBlur={() => onEditBlur?.(sectionId, index)} onSave={(value) => onBlockEdit(sectionId, index, blockToMarkdown(block, value), `Edition d'un paragraphe`)}>
              {renderCommentedText(block.text, blockComments)}
            </EditableText>);
            }
            const blockText = block.type === "table" ? block.headers.join(" ") : block.type === "image" ? block.path : block.type === "math_block" ? block.latex : block.type === "mermaid" ? block.code : block.type === "chart" ? chartTitle(block.spec) : block.type === "visual_asset" ? block.path : block.type === "blockquote" ? block.lines.join(" ") : "text" in block ? block.text : "";
            return (<div className={`markdown-block ${markerMatch ? "has-version" : ""} ${isVersionOpen ? "version-open" : ""} ${assistantChangeMatch ? "assistant-change-target" : ""}`} data-section-id={measurementMode ? undefined : sectionId} data-markdown-block-index={index} data-pagination-block-type={block.type} data-assistant-change-version-id={assistantChangeMatch?.version.id} data-assistant-change-block-id={assistantChangeMatch?.block.id} key={index}>
            {markerMatch && (<button className="version-marker" type="button" title={versionTooltip(markerMatch.version)} onClick={(event) => {
                        event.stopPropagation();
                        onActiveVersionChange((current) => current?.versionId === markerMatch.version.id && current.blockId === markerMatch.block.id
                            ? null
                            : { versionId: markerMatch.version.id, blockId: markerMatch.block.id });
                    }}>
                <Clock3 size={14}/>
              </button>)}
            {commentMode && (<button className="insertion-button" type="button" title="Inserer ici" onClick={(event) => {
                        event.stopPropagation();
                        onInsertionPoint(sectionId, index, blockText, event.currentTarget.getBoundingClientRect());
                    }}>
                <Plus size={14}/>
              </button>)}
            {editMode && (<BlockInsertionControl onInsert={(markdownValue, origin) => onInsertBlock?.(sectionId, index, markdownValue, origin)} onRichInsertion={(rect) => onInsertionPoint(sectionId, index, blockText, rect)}/>)}
            {editMode && block.type !== "h1" && (<button className="block-delete-button" type="button" title={`Supprimer ${blockDeleteLabel(block)}`} onClick={(event) => {
                        event.stopPropagation();
                        onDeleteBlock?.(sectionId, index, blockDeleteLabel(block));
                    }}>
                <Trash2 size={14}/>
              </button>)}
            {content}
            <InlineSuggestionState comments={blockComments} sectionMarkdown={markdown} projectId={projectId} processingCommentIds={processingCommentIds} onAcceptSuggestion={onAcceptSuggestion} onRejectSuggestion={onRejectSuggestion}/>
            {!!blockComments.length && <InlineCommentBadges comments={blockComments}/>}
            {activeHistoryItem && isVersionOpen && (<BlockVersionPopover version={activeHistoryItem.version} block={activeHistoryItem.block} history={activeDisplayHistory} activeIndex={activeDisplayIndex} projectId={projectId} onNavigateVersion={(item) => onActiveVersionChange({ versionId: item.version.id, blockId: item.block.id })} onClose={() => onActiveVersionChange(null)} onRestoreBlock={onRestoreBlock}/>)}
          </div>);
        })}
    </>);
}

function BlockInsertionControl({ onInsert, onRichInsertion }) {
    const [open, setOpen] = useState(false);
    const [kind, setKind] = useState("h2");
    const [value, setValue] = useState("");
    function submit(event) {
        event.preventDefault();
        const clean = value.trim();
        if (!clean)
            return;
        const markdownValue = kind === "h2" ? `## ${clean}` : kind === "bullet" ? `- ${clean}` : clean;
        const label = kind === "h2" ? "sous-partie" : kind === "bullet" ? "puce" : "paragraphe";
        onInsert?.(markdownValue, `Ajout d'une ${label}`);
        setOpen(false);
        setValue("");
    }
    return (<div className={`block-insertion-control ${open ? "open" : ""}`}>
      <button className="insertion-button" type="button" title="Ajouter un titre, du texte ou une image ici" onClick={(event) => {
            event.stopPropagation();
            setOpen((current) => !current);
        }}><Plus size={14}/></button>
      {open && (<form className="block-insertion-menu" onSubmit={submit} onClick={(event) => event.stopPropagation()}>
          <strong>Ajouter après ce bloc</strong>
          <div className="block-insertion-types">
            <button type="button" className={kind === "h2" ? "active" : ""} onClick={() => setKind("h2")}>Sous-partie</button>
            <button type="button" className={kind === "paragraph" ? "active" : ""} onClick={() => setKind("paragraph")}>Texte</button>
            <button type="button" className={kind === "bullet" ? "active" : ""} onClick={() => setKind("bullet")}>Puce</button>
          </div>
          <input autoFocus value={value} onChange={(event) => setValue(event.target.value)} placeholder={kind === "h2" ? "Titre de la sous-partie" : kind === "bullet" ? "Texte de la puce" : "Nouveau paragraphe"}/>
          <div className="block-insertion-actions">
            <button type="submit" disabled={!value.trim()}>Ajouter</button>
            <button type="button" onClick={(event) => {
                setOpen(false);
                onRichInsertion?.(event.currentTarget.getBoundingClientRect());
            }}><ImagePlus size={14}/> Image ou consigne</button>
          </div>
        </form>)}
    </div>);
}

export function EditableText({ as, id, className, text, editable, children, onSave, onEditFocus, onEditBlur }) {
    const editorRef = useRef(null);
    const Tag = as || "p";
    const props = {
        ref: editorRef,
        id,
        className: `${className ?? ""} ${editable ? "editable-text" : ""}`.trim() || undefined,
        contentEditable: editable,
        suppressContentEditableWarning: true,
        onFocus: () => {
            if (editable)
                onEditFocus?.();
        },
        onBlur: (event) => {
            const serialized = inlineMarkdownFromEditable(event.currentTarget);
            const value = as === "figcaption" ? capitalizeCaption(serialized) : serialized;
            if (editable && value !== cleanEditableText(text))
                onSave(value);
            if (editable)
                onEditBlur?.();
        },
        onKeyDown: (event) => {
            if (editable && (event.ctrlKey || event.metaKey) && ["b", "i"].includes(event.key.toLowerCase())) {
                event.preventDefault();
                applyInlineFormat(editorRef.current, event.key.toLowerCase() === "b" ? "bold" : "italic");
                return;
            }
            if (editable && event.key === "Enter") {
                event.preventDefault();
                event.currentTarget.blur();
            }
        }
    };
    const element = <Tag {...props}>{editable ? renderInlineMarkdown(text, false) : children}</Tag>;
    if (!editable)
        return element;
    return (<div className={`inline-edit-shell inline-edit-${Tag}`}>
      <InlineFormatToolbar editorRef={editorRef}/>
      {element}
    </div>);
}

function InlineFormatToolbar({ editorRef }) {
    return (<div className="inline-format-toolbar" role="toolbar" aria-label="Mise en forme du texte" contentEditable={false} onMouseDown={(event) => event.preventDefault()}>
      <button type="button" aria-label="Gras" title="Gras (Ctrl+B)" onClick={() => applyInlineFormat(editorRef.current, "bold")}>
        <Bold size={14}/>
      </button>
      <button type="button" aria-label="Italique" title="Italique (Ctrl+I)" onClick={() => applyInlineFormat(editorRef.current, "italic")}>
        <Italic size={14}/>
      </button>
    </div>);
}

function applyInlineFormat(editor, command) {
    if (!editor || typeof document.execCommand !== "function")
        return;
    if (document.activeElement !== editor)
        editor.focus({ preventScroll: true });
    document.execCommand(command, false);
}

function EditableTableCell({ as, text, editable, alignment, autoFocus = false, onAutoFocusComplete, onSave, onEditFocus, onEditBlur }) {
    const cellRef = useRef(null);
    useEffect(() => {
        if (!autoFocus || !editable || !cellRef.current)
            return;
        cellRef.current.focus();
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(cellRef.current);
        selection?.removeAllRanges();
        selection?.addRange(range);
        onAutoFocusComplete?.();
    }, [autoFocus, editable, onAutoFocusComplete]);
    const props = {
        ref: cellRef,
        className: editable ? "editable-text" : undefined,
        style: alignment && alignment !== "default" ? { textAlign: alignment } : undefined,
        contentEditable: editable,
        suppressContentEditableWarning: true,
        onFocus: () => {
            if (editable)
                onEditFocus?.();
        },
        onBlur: (event) => {
            const value = cleanEditableText(event.currentTarget.textContent ?? "");
            if (editable && value !== cleanEditableText(text))
                onSave(value);
            if (editable)
                onEditBlur?.();
        },
        onKeyDown: (event) => {
            if (editable && event.key === "Enter") {
                event.preventDefault();
                event.currentTarget.blur();
            }
        }
    };
    const children = editable ? text : renderInlineMarkdown(text);
    return as === "th" ? <th {...props}>{children}</th> : <td {...props}>{children}</td>;
}

function BlockVersionPopover({ version, block, history, activeIndex, projectId, onNavigateVersion, onClose, onRestoreBlock }) {
    const beforeImage = block.image_path_before?.split("/").pop();
    const afterImage = block.image_path_after?.split("/").pop();
    const newerVersion = activeIndex > 0 ? history[activeIndex - 1] : null;
    const olderVersion = activeIndex >= 0 && activeIndex < history.length - 1 ? history[activeIndex + 1] : null;
    const beforeText = block.previous_text || block.previous_markdown;
    const afterText = block.current_text || block.current_markdown;
    const beforeDiff = block.previous_markdown || block.previous_text;
    const afterDiff = block.current_markdown || block.current_text;
    return (<div className="version-popover">
      <button className="version-popover-close" type="button" aria-label="Fermer la comparaison" title="Fermer" onClick={(event) => {
            event.stopPropagation();
            onClose?.();
        }}>
        <X size={16}/>
      </button>
      <div className="version-popover-head">
        <span>{sourceLabel(version.source)} - {formatDateTime(version.created_at)}</span>
        <strong>{versionActionLabel(version.action)}</strong>
        <small>{versionBlockLabel(block)}</small>
      </div>
      {history.length > 1 && (<div className="version-nav">
          <button className="button secondary compact-button" disabled={!newerVersion} onClick={() => newerVersion && onNavigateVersion(newerVersion)} type="button">
            Plus recente
          </button>
          <span>{activeIndex + 1} / {history.length}</span>
          <button className="button secondary compact-button" disabled={!olderVersion} onClick={() => olderVersion && onNavigateVersion(olderVersion)} type="button">
            Plus ancienne
          </button>
        </div>)}
      {version.origin && <p className="version-origin">Origine: {version.origin}</p>}
      {beforeImage || afterImage ? (<div className="version-image-diff">
          {beforeImage && (<figure>
              <img src={api.assetUrl(projectId, beforeImage)} alt="Version precedente"/>
              <figcaption>Avant</figcaption>
            </figure>)}
          {afterImage && (<figure>
              <img src={api.assetUrl(projectId, afterImage)} alt="Version actuelle"/>
              <figcaption>Apres</figcaption>
            </figure>)}
        </div>) : (<>
          <VersionTextSnapshots before={beforeText} after={afterText}/>
          <SuggestionDiff before={beforeDiff} after={afterDiff} projectId={projectId}/>
        </>)}
      <div className="version-actions">
        <button className="button compact-button" type="button" onClick={() => onRestoreBlock(version, block)}>
          <RotateCcw size={15}/> Restaurer ce bloc
        </button>
      </div>
    </div>);
}

function InlineCommentBadges({ comments }) {
    return (<div className="block-comments">
      {comments.map((comment) => (<span key={comment.id} className={comment.status} title={comment.comment}>
          {comment.target_type === "insertion"
                ? "Insertion"
                : comment.target_type === "image_edit"
                    ? "Retouche image"
                    : comment.target_type === "image"
                        ? "Image"
                        : comment.target_type === "chart"
                            ? "Graphe"
                            : "Texte"} - {comment.status}
        </span>))}
    </div>);
}

function InlineSuggestionState({ comments, sectionMarkdown, projectId, processingCommentIds, onAcceptSuggestion, onRejectSuggestion }) {
    const active = comments.filter((comment) => !isObsoleteSuggestionForSection(comment, sectionMarkdown) &&
        (processingCommentIds[comment.id] || comment.edit_suggestion?.status === "pending" || comment.edit_suggestion?.status === "ready" || comment.edit_suggestion?.status === "failed"));
    if (!active.length)
        return null;
    return (<div className="inline-suggestions">
      {active.map((comment) => {
            const suggestion = comment.edit_suggestion;
            if (!suggestion)
                return null;
            if (!suggestion || suggestion.status === "pending" || processingCommentIds[comment.id]) {
                return (<div className="inline-suggestion-loading" key={comment.id}>
              <span />
              L'IA traite ce commentaire...
            </div>);
            }
            if (suggestion.status === "failed") {
                return (<div className="suggestion-error" key={comment.id}>
              {suggestion.error || "Suggestion impossible."}
            </div>);
            }
            return (<div className="inline-suggestion" key={comment.id}>
            <CommentSuggestionDiff suggestion={suggestion} projectId={projectId}/>
            <div className="inline-suggestion-actions">
              <button className="button compact-button" onClick={() => onAcceptSuggestion(comment.id)} type="button">
                <CheckCircle size={15}/> Accepter
              </button>
              <button className="button secondary compact-button" onClick={() => onRejectSuggestion(comment.id)} type="button">
                Refuser
              </button>
            </div>
          </div>);
        })}
    </div>);
}

function UserInputQueue({ comments, clarificationQuestions, showPlanReview, onCommentClick, onQuestionClick, onPlanClick }) {
    const visibleComments = comments.slice(0, 4);
    const hiddenCount = Math.max(0, comments.length - visibleComments.length);
    return (<section className="user-input-queue" aria-label="Actions utilisateur attendues">
      <div className="user-input-queue-head">
        <Clock3 size={16}/>
        <strong>À valider</strong>
        <span>{comments.length + clarificationQuestions.length + (showPlanReview ? 1 : 0)} attente(s)</span>
      </div>
      <div className="user-input-queue-items">
        {showPlanReview && (<button type="button" onClick={onPlanClick}>
            <CheckCircle size={15}/>
            <span>Plan du rapport</span>
            <small>Valider ou modifier</small>
          </button>)}
        {clarificationQuestions.slice(0, 2).map((question) => (<button key={question.id} type="button" onClick={() => onQuestionClick(question)}>
            <MessageSquare size={15}/>
            <span>{question.blocking ? "Arbitrage analyse" : "Question analyse"}</span>
            <small>{question.prompt}</small>
          </button>))}
        {visibleComments.map((comment) => (<button key={comment.id} type="button" onClick={() => onCommentClick(comment)}>
            {comment.target_type === "chart" ? <BarChart3 size={15}/> : comment.target_type === "image_edit" ? <Wand2 size={15}/> : <MessageSquare size={15}/>}
            <span>{inputQueueCommentLabel(comment)}</span>
            <small>{comment.section_id || "Rapport"}</small>
          </button>))}
        {hiddenCount > 0 && (<button type="button" onClick={() => visibleComments[0] && onCommentClick(visibleComments[0])}>
            <Plus size={15}/>
            <span>{hiddenCount} autre(s)</span>
            <small>Voir commentaires</small>
          </button>)}
      </div>
    </section>);
}

function CommentSuggestionDiff({ suggestion, projectId }) {
    let before = suggestion?.original_excerpt ?? "";
    let after = suggestion?.suggested_excerpt ?? "";
    const excerptBeforeTables = tablePreviewBlocksFromMarkdown(before);
    const excerptAfterTables = tablePreviewBlocksFromMarkdown(after);
    if (excerptBeforeTables.length && !excerptAfterTables.length) {
        const fullAfterTables = tablePreviewBlocksFromMarkdown(suggestion?.suggested_markdown ?? "");
        const closestAfterTable = closestTableBlock(excerptBeforeTables[0], fullAfterTables);
        if (closestAfterTable) {
            before = excerptBeforeTables[0].raw;
            after = closestAfterTable.raw;
        }
    }
    return <SuggestionDiff before={before} after={after} projectId={projectId}/>;
}

function closestTableBlock(reference, candidates) {
    if (!reference || !candidates.length)
        return null;
    return candidates.reduce((closest, candidate) => {
        if (!closest)
            return candidate;
        return lineSimilarity(reference.raw, candidate.raw) > lineSimilarity(reference.raw, closest.raw)
            ? candidate
            : closest;
    }, null);
}

function SuggestionDiff({ before, after, projectId }) {
    const rows = smartDiffRows(before, after);
    const summary = diffSummary(rows);
    const beforeImages = projectId ? markdownImageRefs(before) : [];
    const afterImages = projectId ? markdownImageRefs(after) : [];
    const hasImagePreview = Boolean(projectId && (beforeImages.length || afterImages.length));
    const beforeChart = chartPreviewBlockFromMarkdown(before);
    const afterChart = chartPreviewBlockFromMarkdown(after);
    const hasChartPreview = Boolean(beforeChart || afterChart);
    const beforeTables = tablePreviewBlocksFromMarkdown(before);
    const afterTables = tablePreviewBlocksFromMarkdown(after);
    const hasTablePreview = Boolean(beforeTables.length || afterTables.length);
    return (<div className="suggestion-diff">
      <div className="suggestion-diff-head">
        <strong>Changements reels</strong>
        <span>{summary}</span>
      </div>
      {hasChartPreview && (<ChartSuggestionPreview beforeChart={beforeChart} afterChart={afterChart} projectId={projectId}/>)}
      {hasTablePreview && (<TableSuggestionPreview beforeTables={beforeTables} afterTables={afterTables}/>)}
      {hasImagePreview && (<div className="suggestion-image-preview">
          <ImageDiffColumn title="Avant" images={beforeImages} projectId={projectId} emptyLabel="Aucune image"/>
          <ImageDiffColumn title="Apres" images={afterImages} projectId={projectId} emptyLabel="Aucune image"/>
        </div>)}
      {!hasChartPreview && (<div className="suggestion-smart-diff">
        {rows.length ? (rows.map((row, index) => {
            if (row.type === "ellipsis") {
                return (<div className="suggestion-diff-row ellipsis" key={index}>
                  ...
                </div>);
            }
            if (row.type === "context") {
                return (<div className="suggestion-diff-row context" key={index}>
                  <span className="diff-prefix"> </span>
                  <span>{row.text}</span>
                </div>);
            }
            if (row.type === "removed") {
                return (<div className="suggestion-diff-row removed" key={index}>
                  <span className="diff-prefix">-</span>
                  <del>{row.text}</del>
                </div>);
            }
            if (row.type === "added") {
                return (<div className="suggestion-diff-row added" key={index}>
                  <span className="diff-prefix">+</span>
                  <ins>{row.text}</ins>
                </div>);
            }
            return (<div className="suggestion-diff-row inline" key={index}>
                <span className="diff-prefix">~</span>
                <span>
                  {row.segments.map((segment, segmentIndex) => {
                    if (segment.type === "removed")
                        return <del key={segmentIndex}>{segment.text}</del>;
                    if (segment.type === "added")
                        return <ins key={segmentIndex}>{segment.text}</ins>;
                    return <span key={segmentIndex}>{segment.text}</span>;
                })}
                </span>
              </div>);
        })) : (<span>Aucune modification textuelle detectee.</span>)}
      </div>)}
    </div>);
}

function TableSuggestionPreview({ beforeTables, afterTables }) {
    return (<div className="suggestion-table-preview">
      <TableSuggestionColumn title="Avant" tables={beforeTables} emptyLabel="Aucun tableau"/>
      <TableSuggestionColumn title="Apres" tables={afterTables} emptyLabel="Tableau supprime ou invalide"/>
    </div>);
}

function TableSuggestionColumn({ title, tables, emptyLabel }) {
    return (<div className="suggestion-table-column">
      <strong>{title}</strong>
      {tables.length ? tables.map((table, tableIndex) => (<div className="markdown-table-wrap" key={tableIndex}>
          <table className="markdown-table">
            <thead>
              <tr>
                {table.headers.map((cell, cellIndex) => (<th key={cellIndex}>{renderInlineMarkdown(cell)}</th>))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, rowIndex) => (<tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (<td key={cellIndex}>{renderInlineMarkdown(cell)}</td>))}
                </tr>))}
            </tbody>
          </table>
        </div>)) : <span>{emptyLabel}</span>}
    </div>);
}

function ChartSuggestionPreview({ beforeChart, afterChart, projectId }) {
    const changes = chartSpecDiffItems(beforeChart?.spec, afterChart?.spec);
    return (<div className="suggestion-chart-preview">
      <div className="suggestion-chart-columns">
        <ChartSuggestionColumn title="Avant" block={beforeChart} projectId={projectId}/>
        <ChartSuggestionColumn title="Apres" block={afterChart} projectId={projectId}/>
      </div>
      {!!changes.length && (<ul className="suggestion-chart-fields">
          {changes.map((item) => (<li key={item.label}>
              <strong>{item.label}</strong>
              <span>{item.before || "vide"} -> {item.after || "vide"}</span>
            </li>))}
        </ul>)}
    </div>);
}

function ChartSuggestionColumn({ title, block, projectId }) {
    return (<div className="suggestion-chart-column">
      <strong>{title}</strong>
      {block ? (<>
          <ChartPreview block={block} projectId={projectId} sectionId={`suggestion-${title.toLowerCase()}`} editable={false}/>
          {block.error && <span className="suggestion-chart-error">{block.error}</span>}
        </>) : (<span>Aucun graphe</span>)}
    </div>);
}

export {
    BlockInsertionControl,
    BlockVersionPopover,
    ChartSuggestionColumn,
    ChartSuggestionPreview,
    CommentSuggestionDiff,
    EditableTableCell,
    FullReportPreview,
    InlineCommentBadges,
    InlineFormatToolbar,
    InlineSuggestionState,
    MarkdownBlock,
    OutlineLivePreview,
    ReportAttributionToggle,
    ReportOutlinePanel,
    SectionStructureToolbar,
    SuggestionDiff,
    TableSuggestionColumn,
    TableSuggestionPreview,
    UserInputQueue,
    VersionBlockPreview,
    VersionPanel,
    VersionTextSnapshots,
    applyInlineFormat,
    closestTableBlock,
    fallbackReportPagination,
    paginateBlockHeights,
    reportPaginationRevision,
    reportPreviewFontFaceCss,
    reportTocEntries
};
